import { UUID } from 'crypto';

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { UNUSED_DATA_RETENTION_SECONDS } from '@/utils/constants';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import {
  AddImageToAlertRequest,
  Alert,
  CreateAlertRequest,
  DeleteAlertRequest,
  RemoveImageFromAlertRequest,
  ReportImage,
  UpdateAlertRequest,
  UpdateMultipleNotesRequest,
  UpdateMultipleNotesResponse
} from '@/types/alert';
import { AlertResponse } from '@/types/analytics';
import { CountersResponse, CreateCountersRequest, UpdateCountersRequest } from '@/types/counters';
import { DeviceLeadsRecallResponse, DeviceRecallResponse, MatchDevice } from '@/types/matchDevice';
import {
  AddAttachmentsRequest,
  GetReportAttachmentsResponse,
  GetSelectedReportsResponse,
  ToggleSelectedReportInFinalPdfRequest,
  UpdateInFinalPdfRequestDto,
  UpdateInFinalPdfResponse
} from '@/types/reportAttachment';
import {
  ApplyReportTemplateRequest,
  FetchReportTemplatesRequest,
  FetchReportTemplatesResponse,
  ReplaceReportTemplateRequest,
  ReplaceReportTemplateResponse,
  ReportTemplateResponse
} from '@/types/reportTemplate';
import {
  CreateReportDto,
  GetTransmissionReportDiagnosesParams,
  MiniMenuReportsRequestDto,
  MiniTransmissionReport,
  OcrStatusResponse,
  ProgrammedParameters,
  ReportColor,
  ReportInClinicEpisode,
  ReportType,
  ReviewPendingReportsResponse,
  SignReportRequest,
  SignReportResponse,
  TransmissionReportDetailsResponse,
  TransmissionReportDiagnosisListResponse,
  TransmissionReportGetDefaultIdRequestDto,
  UpdateProgrammedParametersRequest,
  UpdateTransmissionReportParams
} from '@/types/transmissionReport';
import {
  UpdateMultipleOverridesRequest,
  UpdateMultipleOverridesResponse,
  VendorReportImageResponse,
  VtLeadVendor
} from '@/types/vendorTransmissionReport';

import { initializeForm } from '../../form/transmissionReportFormSlice';
import { billingApiSlice } from '../billingApiSlice';

export interface TriageAuditLogTrigger {
  text: string;
  triggered: boolean;
  confidence: number;
  explanation: string;
}

export interface TriageAuditLogReviewEntry {
  isCorrect: boolean;
  notes?: string;
}

export interface TriageAuditLog {
  id: string;
  outcome: string;
  reason: string;
  response: Record<string, TriageAuditLogTrigger>;
  thoughts: string;
  processingTimeMs: number;
  inputTokens: number;
  outputTokens: number;
  thoughtTokens: number;
  modelRequestId: string;
  modelName: string;
  review: Record<string, TriageAuditLogReviewEntry> | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  transmissionId: string;
  organizationId: string;
  alertProtocolId: string;
  triagePromptId: string;
  createdAt: string;
}

export interface TriageAuditLogResponse {
  triageAuditLog: TriageAuditLog;
}

export const transmissionReportApiSlice = createApi({
  reducerPath: 'transmissionReportApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.TransmissionReport],
  endpoints: (builder) => ({
    getTransmissionReportDetails: builder.query<TransmissionReportDetailsResponse, number>({
      query: (id) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/${id}`,
        method: 'GET'
      }),
      keepUnusedDataFor: 1,
      providesTags: (_result, _error, id) => [{ type: API_SLICE.TransmissionReport, id }],
      onQueryStarted: async (_id, { dispatch, queryFulfilled, getState }) => {
        const { data: transmissionReportDetails } = await queryFulfilled;
        if (transmissionReportDetails) {
          const userRole = (getState() as RootState).auth.user?.role;
          const { transmissionReport } = transmissionReportDetails;
          dispatch(initializeForm({ transmissionReport, userRole }));
        }
      }
    }),
    getDefaultTransmissionReportId: builder.query<
      { transmissionReportId: number; color: ReportColor },
      TransmissionReportGetDefaultIdRequestDto
    >({
      query: (body) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/v2/default`,
        method: 'POST',
        body: body
      }),
      keepUnusedDataFor: 0
    }),
    patchTransmissionReport: builder.mutation<
      TransmissionReportDetailsResponse,
      { id: number } & Partial<UpdateTransmissionReportParams>
    >({
      query: ({ id, ...data }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/${id}`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, { id, color, signingProviderId }) =>
        color || signingProviderId
          ? [
              { type: API_SLICE.TransmissionReport, id },
              { type: API_SLICE.TransmissionReport, id: 'mini_menu_reports' },
              { type: API_SLICE.TransmissionReport, id: 'paginated_reports' }
            ]
          : [{ type: API_SLICE.TransmissionReport, id }]
    }),
    updateMultipleOverrides: builder.mutation<
      UpdateMultipleOverridesResponse,
      UpdateMultipleOverridesRequest
    >({
      query: ({ transmissionReportId, overrides }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.UPDATE_MULTIPLE_OVERRIDES(transmissionReportId),
        method: 'PATCH',
        body: overrides
      }),
      invalidatesTags: (_result, _error, { transmissionReportId }) => [
        { type: API_SLICE.TransmissionReport, id: transmissionReportId }
      ]
    }),
    getLeadVendorsList: builder.query<Array<VtLeadVendor>, void>({
      query: () => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.LEAD_VENDOR_LIST,
        method: 'GET'
      }),
      // save data for 24 hours
      keepUnusedDataFor: UNUSED_DATA_RETENTION_SECONDS
    }),
    addImageToAlert: builder.mutation<ReportImage, AddImageToAlertRequest>({
      query: ({ transmissionReportId, alertId, transmissionReportAddAlertImageParams }) => ({
        url: API_ENDPOINTS.ALERTS.UPLOAD_IMAGE(transmissionReportId, alertId),
        method: 'POST',
        body: transmissionReportAddAlertImageParams
      })
    }),
    getVendorReportImage: builder.query<VendorReportImageResponse, number>({
      query: (id) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.VENDOR_REPORT_IMAGE(id),
        method: 'GET'
      })
    }),
    deleteImageFromAlert: builder.mutation<void, RemoveImageFromAlertRequest>({
      query: ({ transmissionReportId, alertId, imageId }) => ({
        url: API_ENDPOINTS.ALERTS.DELETE_IMAGE(transmissionReportId, alertId, imageId),
        method: 'DELETE'
      })
    }),
    createAlert: builder.mutation<Alert, CreateAlertRequest>({
      query: ({ params, reportId }) => ({
        url: API_ENDPOINTS.ALERTS.CREATE_ALERT(reportId),
        method: 'POST',
        body: params
      })
    }),
    updateAlertTitle: builder.mutation<AlertResponse, UpdateAlertRequest>({
      query: ({ params, reportId, alertId }) => ({
        url: API_ENDPOINTS.ALERTS.UPDATE_ALERT(reportId, alertId),
        method: 'PATCH',
        body: params
      })
    }),
    getReportAttachments: builder.query<GetReportAttachmentsResponse, number>({
      query: (id) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.ATTACHMENTS(id),
        method: 'GET'
      }),
      providesTags: (_result, _error, id) => [
        { type: API_SLICE.TransmissionReport, id: `attachment_${id}` }
      ]
    }),
    getSelectedReports: builder.query<GetSelectedReportsResponse, number>({
      query: (transmissionId) => ({
        url: `transmission-reports/v2/${transmissionId}/selected-reports`,
        method: 'GET'
      }),
      providesTags: (_result, _error, id) => [
        { type: API_SLICE.TransmissionReport, id: `selected_reports_${id}` }
      ]
    }),
    toggleSelectedReportInFinalPdf: builder.mutation<any, ToggleSelectedReportInFinalPdfRequest>({
      query: (data) => ({
        url: '/transmission-reports/v2/selected-reports/in-final-pdf',
        method: 'PATCH',
        body: data
      })
    }),
    addReportAttachments: builder.mutation<void, AddAttachmentsRequest>({
      query: ({ reportId, files, deletedFiles }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files[]', file));
        deletedFiles.forEach((file) => formData.append('deletedFiles', file));
        return {
          url: API_ENDPOINTS.TRANSMISSION_REPORTS.ATTACHMENTS(reportId),
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: `attachment_${reportId}` },
        { type: API_SLICE.TransmissionReport, id: `vendor_reports_${reportId}` }
      ]
    }),
    updateInFinalPdf: builder.mutation<UpdateInFinalPdfResponse, UpdateInFinalPdfRequestDto>({
      query: (data) => ({
        url: 'transmission-reports/v2/attachments/in-final-pdf',
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, _arg) => [{ type: API_SLICE.TransmissionReport }]
    }),
    updateMultipleNotes: builder.mutation<UpdateMultipleNotesResponse, UpdateMultipleNotesRequest>({
      query: ({ transmissionReportId, notes }) => ({
        url: API_ENDPOINTS.NOTES.UPDATE_MULTIPLE(transmissionReportId),
        method: 'POST',
        body: { notes }
      }),
      invalidatesTags: (_result, _error, { transmissionReportId }) => [
        { type: API_SLICE.TransmissionReport, id: transmissionReportId }
      ]
    }),
    deleteAlert: builder.mutation<AlertResponse, DeleteAlertRequest>({
      query: ({ reportId, alertId }) => ({
        url: API_ENDPOINTS.ALERTS.DELETE_ALERT(reportId, alertId),
        method: 'DELETE'
      }),
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            transmissionReportApiSlice.util.updateQueryData(
              'getTransmissionReportDetails',
              args.reportId,
              (draft) => {
                const alertIndex = draft?.transmissionReport.alerts.findIndex(
                  (alert) => alert.id === args.alertId
                );
                if (alertIndex !== -1 && draft) {
                  draft.transmissionReport.alerts.splice(alertIndex, 1);
                } else throw new Error('Alert not found');
              }
            )
          );
        } catch (error) {
          // invalidate the query to refetch the data
          dispatch(
            transmissionReportApiSlice.util.invalidateTags([
              { type: API_SLICE.TransmissionReport, id: args.reportId }
            ])
          );
        }
      }
    }),
    signReport: builder.mutation<SignReportResponse, SignReportRequest>({
      query: ({ reportId, signDateParams }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.SIGN(reportId),
        method: 'PATCH',
        body: signDateParams
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ],
      async onQueryStarted({ reportId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }])
          );
        } catch (error) {
          console.debug('Failed to invalidate billing cache tags after report update:', error);
        }
      }
    }),
    getReportTemplates: builder.query<FetchReportTemplatesResponse, FetchReportTemplatesRequest>({
      query: ({ reportId, deviceType, vendor }) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.LIST(reportId),
        method: 'GET',
        params: { deviceType, vendor }
      })
    }),
    getOcrStatus: builder.query<OcrStatusResponse, number>({
      query: (transmissionReportId) => ({
        url: `/transmission-reports/ocr-status/${transmissionReportId}`,
        method: 'GET'
      }),
      keepUnusedDataFor: 0
    }),
    getReportTemplatesWithMappings: builder.query<
      FetchReportTemplatesResponse,
      FetchReportTemplatesRequest
    >({
      query: ({ reportId, deviceType, vendor, type }) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.LIST_V2(reportId),
        method: 'GET',
        params: { deviceType, vendor, type }
      })
    }),
    applyReportTemplate: builder.mutation<ReportTemplateResponse, ApplyReportTemplateRequest>({
      query: ({ reportId, templateId, parsedTemplateobject }) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.APPLY,
        method: 'POST',
        body: { reportId, templateId, parsedTemplateobject }
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ]
    }),
    removeReportTemplate: builder.mutation<ReportTemplateResponse, number>({
      query: (reportId) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.REMOVE,
        method: 'POST',
        body: { reportId }
      }),
      invalidatesTags: (_result, _error, reportId) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ]
    }),
    replaceReportTemplate: builder.mutation<
      ReplaceReportTemplateResponse,
      ReplaceReportTemplateRequest
    >({
      query: ({ reportId, templateId, parsedTemplateobject }) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.REPLACE,
        method: 'POST',
        body: { reportId, templateId, parsedTemplateobject }
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ]
    }),
    updateReportColor: builder.mutation<
      TransmissionReportDetailsResponse,
      { id: number } & Partial<UpdateTransmissionReportParams>
    >({
      query: ({ id, ...data }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/${id}`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, { id, color }) =>
        color
          ? [
              { type: API_SLICE.TransmissionReport, id },
              { type: API_SLICE.TransmissionReport, id: 'mini_menu_reports' },
              { type: API_SLICE.TransmissionReport, id: 'paginated_reports' }
            ]
          : [{ type: API_SLICE.TransmissionReport, id }]
    }),
    updateProgrammedParameters: builder.mutation<
      ProgrammedParameters,
      UpdateProgrammedParametersRequest
    >({
      query: ({ transmissionReportId, programmedParametersUpdateParams }) => ({
        url: API_ENDPOINTS.PROGRAMMED_PARAMETERS.UPDATE(transmissionReportId),
        method: 'PATCH',
        body: programmedParametersUpdateParams
      }),
      invalidatesTags: (_result, _error, { transmissionReportId }) => [
        { type: API_SLICE.TransmissionReport, id: transmissionReportId }
      ]
    }),
    addCounters: builder.mutation<CountersResponse, CreateCountersRequest>({
      query: ({ transmissionReportId, transmissionReportCountersCreateParams }) => ({
        url: API_ENDPOINTS.COUNTERS.CREATE(transmissionReportId),
        method: 'POST',
        body: transmissionReportCountersCreateParams
      }),
      invalidatesTags: (_result, _error, { transmissionReportId }) => [
        { type: API_SLICE.TransmissionReport, id: transmissionReportId }
      ]
    }),
    updateCounters: builder.mutation<CountersResponse, UpdateCountersRequest>({
      query: ({ transmissionReportId, transmissionReportCountersUpdateParams }) => ({
        url: API_ENDPOINTS.COUNTERS.UPDATE(transmissionReportId),
        method: 'PATCH',
        body: transmissionReportCountersUpdateParams
      }),
      invalidatesTags: (_result, _error, { transmissionReportId }) => [
        { type: API_SLICE.TransmissionReport, id: transmissionReportId }
      ]
    }),
    patchReportInClinicEpisode: builder.mutation<
      { message: string; success: boolean },
      { reportId: number } & Partial<ReportInClinicEpisode>
    >({
      query: ({ reportId, ...data }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.INCLINIC_EPISODE(reportId),
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ]
    }),
    applyPhysicianInterpretation: builder.mutation<
      { reportId: number; templateId: string },
      { reportId: number; templateId: UUID | null }
    >({
      query: ({ reportId, templateId }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.APPLY_PHYSICIAN_TEMPLATE(reportId),
        method: 'POST',
        body: { templateId }
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ]
    }),
    updateDevice: builder.mutation<
      { success: boolean; message: string },
      { patientId: string; reportId: number | undefined } & Partial<MatchDevice>
    >({
      query: ({ patientId, reportId, ...data }) => ({
        url: API_ENDPOINTS.PATIENTS.UPDATE_DEVICE(patientId),
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ]
    }),
    getMiniMenuReports: builder.infiniteQuery<
      { data: MiniTransmissionReport[]; total: number; count: number },
      MiniMenuReportsRequestDto,
      number
    >({
      query: ({ queryArg, pageParam = 0 }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.LIST,
        method: 'POST',
        body: { ...queryArg, offset: pageParam }
      }),
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (_lastPage, allPages, lastOffset, _allOffsets, q) => {
          const total = allPages[0].total;
          const next = lastOffset + q.limit;
          return next < total ? next : undefined;
        }
      },
      providesTags: (_result, _error, { color }) => [
        { type: API_SLICE.TransmissionReport, id: `mini_menu_reports_${color}` },
        { type: API_SLICE.TransmissionReport, id: `mini_menu_reports` }
      ]
    }),
    getPaginatedMiniMenuReports: builder.query<
      { data: MiniTransmissionReport[]; total: number; count: number },
      MiniMenuReportsRequestDto
    >({
      query: (queryArg) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.LIST,
        method: 'POST',
        body: queryArg
      }),
      providesTags: (_result, _error, { color, isPendingReview }) => {
        if (isPendingReview) {
          return [{ type: API_SLICE.TransmissionReport, id: 'review_pending_reports' }];
        }
        return [
          { type: API_SLICE.TransmissionReport, id: `paginated_reports_${color}` },
          { type: API_SLICE.TransmissionReport, id: `paginated_reports` }
        ];
      }
    }),
    getReviewPendingReports: builder.query<ReviewPendingReportsResponse, MiniMenuReportsRequestDto>(
      {
        query: (queryArg) => ({
          url: API_ENDPOINTS.TRANSMISSION_REPORTS.LIST,
          method: 'POST',
          body: {
            ...queryArg,
            isPendingReview: true,
            color: undefined
          }
        }),
        providesTags: () => [{ type: API_SLICE.TransmissionReport, id: 'review_pending_reports' }]
      }
    ),
    createReport: builder.mutation<any, CreateReportDto>({
      query: (payload) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.CREATE_TRANSMISSION_REPORT,
        method: 'POST',
        body: payload
      })
    }),
    createInclinicReport: builder.mutation<any, FormData>({
      query: (payload: FormData) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.CREATE_TRANSMISSION_INCLINIC_REPORT,
        method: 'POST',
        body: payload
      })
    }),
    getTransmissionReportDiagnoses: builder.infiniteQuery<
      TransmissionReportDiagnosisListResponse,
      GetTransmissionReportDiagnosesParams,
      number
    >({
      query: ({ queryArg, pageParam = 1 }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/${queryArg.reportId}/diagnoses`,
        method: 'GET',
        params: {
          limit: queryArg.limit ?? 50,
          page: pageParam
        }
      }),
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          const nextPage = lastPageParam + 1;
          return nextPage <= lastPage.totalPages ? nextPage : undefined;
        }
      },
      providesTags: (_result, _error, queryArg) => [
        { type: API_SLICE.TransmissionReport, id: `${queryArg.reportId}-diagnoses` }
      ]
    }),
    addTransmissionReportDiagnosisCode: builder.mutation<
      void,
      { reportId: number; diagnosisCode: string }
    >({
      query: ({ reportId, diagnosisCode }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/signed/${reportId}/add-diagnosis`,
        method: 'POST',
        body: { diagnosisCode }
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: `${reportId}-diagnoses` }
      ]
    }),
    deleteTransmissionReportDiagnosisCode: builder.mutation<
      void,
      { reportId: number; diagnosisCode: string }
    >({
      query: ({ reportId, diagnosisCode }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/signed/${reportId}/delete-diagnosis`,
        method: 'DELETE',
        body: { diagnosisCode }
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: `${reportId}-diagnoses` }
      ]
    }),
    addDevice: builder.mutation<
      MatchDevice,
      { patientId: string; reportId: number | undefined } & Partial<MatchDevice>
    >({
      query: ({ patientId, ...data }) => ({
        url: API_ENDPOINTS.PATIENTS.ADD_DEVICE(patientId),
        method: 'POST',
        body: data
      })
    }),
    getDeviceRecall: builder.query<DeviceRecallResponse, string>({
      query: (deviceId) => ({
        url: API_ENDPOINTS.DEVICE_RECALL.GET(deviceId),
        method: 'GET'
      })
    }),
    getDeviceLeadsRecall: builder.query<DeviceLeadsRecallResponse[], string>({
      query: (deviceId) => ({
        url: API_ENDPOINTS.DEVICE_RECALL.GET_LEADS(deviceId),
        method: 'GET'
      })
    }),
    sendToProvider: builder.mutation<{ success: boolean; message: string }, number>({
      query: (transmissionReportId) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.SEND_TO_PROVIDER(transmissionReportId),
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, transmissionReportId) => [
        { type: API_SLICE.TransmissionReport, id: transmissionReportId }
      ]
    }),
    updateTransmissionType: builder.mutation<
      TransmissionReportDetailsResponse,
      { id: number; transmissionType: ReportType }
    >({
      query: ({ id, transmissionType }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.UPDATE_REPORT_TYPE(id),
        method: 'PATCH',
        body: { transmissionType }
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: API_SLICE.TransmissionReport, id }]
    }),
    startReview: builder.mutation<void, number>({
      query: (transmissionReportId) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.START_REVIEW(transmissionReportId),
        method: 'POST'
      })
    }),
    heartbeat: builder.mutation<void, number>({
      query: (transmissionReportId) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.HEARTBEAT(transmissionReportId),
        method: 'POST'
      })
    }),
    finishReview: builder.mutation<void, number>({
      query: (transmissionReportId) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.FINISH_REVIEW(transmissionReportId),
        method: 'POST'
      })
    }),
    getTriageAuditLog: builder.query<TriageAuditLogResponse, string>({
      query: (triageAuditLogId) => ({
        url: `/triage-audit-logs/${triageAuditLogId}`,
        method: 'GET'
      })
    }),
    submitTriageReview: builder.mutation<
      void,
      {
        triageAuditLogId: string;
        body: Record<string, { isCorrect: boolean; notes?: string }>;
      }
    >({
      query: ({ triageAuditLogId, body }) => ({
        url: `/triage-audit-logs/${triageAuditLogId}/submit-review`,
        method: 'PATCH',
        body
      })
    })
  })
});

export const {
  useGetTransmissionReportDetailsQuery,
  useUpdateMultipleOverridesMutation,
  useUpdateMultipleNotesMutation,
  useGetLeadVendorsListQuery,
  useAddImageToAlertMutation,
  useCreateAlertMutation,
  useGetVendorReportImageQuery,
  useUpdateAlertTitleMutation,
  useDeleteImageFromAlertMutation,
  useAddReportAttachmentsMutation,
  useGetReportAttachmentsQuery,
  useGetSelectedReportsQuery,
  useToggleSelectedReportInFinalPdfMutation,
  useUpdateInFinalPdfMutation,
  useDeleteAlertMutation,
  useSignReportMutation,
  useGetReportTemplatesQuery,
  useGetOcrStatusQuery,
  useGetReportTemplatesWithMappingsQuery,
  useApplyReportTemplateMutation,
  useRemoveReportTemplateMutation,
  useReplaceReportTemplateMutation,
  useUpdateReportColorMutation,
  useUpdateProgrammedParametersMutation,
  useAddCountersMutation,
  useUpdateCountersMutation,
  usePatchReportInClinicEpisodeMutation,
  usePatchTransmissionReportMutation,
  useGetDefaultTransmissionReportIdQuery,
  useApplyPhysicianInterpretationMutation,
  useUpdateDeviceMutation,
  useGetMiniMenuReportsInfiniteQuery,
  useGetPaginatedMiniMenuReportsQuery,
  useLazyGetPaginatedMiniMenuReportsQuery,
  useCreateReportMutation,
  useCreateInclinicReportMutation,
  useGetTransmissionReportDiagnosesInfiniteQuery,
  useAddTransmissionReportDiagnosisCodeMutation,
  useDeleteTransmissionReportDiagnosisCodeMutation,
  useGetReviewPendingReportsQuery,
  useAddDeviceMutation,
  useGetDeviceRecallQuery,
  useSendToProviderMutation,
  useGetDeviceLeadsRecallQuery,
  useUpdateTransmissionTypeMutation,
  useStartReviewMutation,
  useHeartbeatMutation,
  useFinishReviewMutation,
  useGetTriageAuditLogQuery,
  useSubmitTriageReviewMutation
} = transmissionReportApiSlice;
